import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Dialogo de confirmación estilizado (reemplaza window.confirm / alert nativos).
 *
 * Uso típico:
 *   <ConfirmDialog
 *     isOpen={open}
 *     title="¿Eliminar etiqueta?"
 *     message="Se quitará de las tareas que la tengan. La acción no se puede deshacer."
 *     variant="danger"
 *     confirmLabel="Eliminar"
 *     onConfirm={() => { ... }}
 *     onClose={() => setOpen(false)}
 *   />
 *
 * variant: 'danger' (default) | 'warning' | 'info'
 */
const VARIANTS = {
  danger: {
    iconBg: 'bg-rose-50',
    iconColor: 'text-rose-500',
    confirmBg: 'bg-rose-600 hover:bg-rose-700',
  },
  warning: {
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
    confirmBg: 'bg-amber-600 hover:bg-amber-700',
  },
  info: {
    iconBg: 'bg-cyan-50',
    iconColor: 'text-cyan-600',
    confirmBg: 'bg-slate-900 hover:bg-slate-800',
  },
};

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  variant = 'danger',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  onConfirm,
  onClose,
}) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      // Enter solo dispara cuando el foco vive dentro del diálogo. Asi un
      // Enter perdido en un input del fondo no termina confirmando una accion
      // destructiva sin querer.
      if (e.key === 'Enter') {
        const inside = dialogRef.current && dialogRef.current.contains(document.activeElement);
        if (!inside) return;
        const tag = document.activeElement?.tagName?.toLowerCase();
        // Si el foco esta en textarea/input editable dentro del dialog, dejar
        // que el campo maneje Enter (raro en confirmaciones, pero defensivo).
        if (tag === 'textarea' || (tag === 'input' && document.activeElement.type !== 'button')) return;
        onConfirm?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, onConfirm]);

  if (!isOpen) return null;

  const v = VARIANTS[variant] || VARIANTS.danger;

  const content = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      >
        <motion.div
          ref={dialogRef}
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${v.iconBg} flex items-center justify-center`}>
                <AlertTriangle size={18} className={v.iconColor} strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
                  {title}
                </h3>
                {message && (
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    {message}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 -mr-1 -mt-1 p-1 rounded transition-colors"
                aria-label="Cerrar"
              >
                <X size={14} />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 bg-slate-50/60 border-t border-slate-200/70">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              autoFocus
              className={`px-3.5 py-1.5 text-sm font-medium text-white rounded-md transition-colors active:scale-[0.98] ${v.confirmBg}`}
            >
              {confirmLabel}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default ConfirmDialog;
